import { Box, Tooltip, Typography } from '@mui/material'
import { Group as GroupIcon, Person as PersonIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ProgressBar } from '@/components/common/ProgressBar'
import type { WidgetProps } from './WidgetProps'
import { ROUTES } from '@/utils/constants'

interface AssignmentItem {
  assignment_id: number
  name: string
  /** Имя группы или ученика; null — получатель удалён */
  recipient_name: string | null
  is_group: boolean
  /** Тема закладки; null — к программе ещё не приступали */
  current_theme: string | null
  themes_total: number
  themes_done: number
}

/**
 * Виджет учителя «Программы групп»: кто на какой теме сейчас.
 *
 * Строка = одно активное назначение. Клик ведёт на карточку назначения —
 * это же и самый короткий путь учителя к программе конкретного ученика.
 */
export function GroupProgramsWidget({ payload }: WidgetProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const items = (payload.items ?? []) as AssignmentItem[]

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('programs.widget.empty')}
      </Typography>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {items.map((item) => {
        const percent = item.themes_total > 0 ? (item.themes_done / item.themes_total) * 100 : 0

        return (
          <Box
            key={item.assignment_id}
            onClick={() => navigate(ROUTES.PROGRAM_ASSIGNMENT(item.assignment_id))}
            sx={{
              cursor: 'pointer',
              borderRadius: 1,
              px: 0.5,
              py: 0.25,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              {item.is_group ? (
                <GroupIcon fontSize="small" color="action" />
              ) : (
                <PersonIcon fontSize="small" color="action" />
              )}
              <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
                {item.recipient_name ?? t('programs.widget.recipientGone')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {item.themes_done}/{item.themes_total}
              </Typography>
            </Box>

            {/* Название программы во всплывающей подсказке: в строку виджета
                помещается либо оно, либо текущая тема — тема важнее */}
            <Tooltip title={item.name}>
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {item.current_theme ?? t('programs.assignments.notStarted')}
              </Typography>
            </Tooltip>

            <ProgressBar value={percent} height={4} />
          </Box>
        )
      })}
    </Box>
  )
}
